// Map 2.0 layer definitions + persistence. The default map is deliberately
// simple: live SpotDrop spots, free parking and paid/public parking only.

const STORAGE_KEY = "spotdrop_map_layers_v3";
const NEARBY_KEY = "spotdrop_nearby_prefs_v1";

// Kinds that come from the backend `parking_places` collection.
export const PLACE_LAYER_KINDS = [
  "free_parking",
  "paid_parking",
  "car_park",
  "motorcycle_parking",
  "residents_only",
  "no_parking",
  "loading_bay",
];

export const RESTRICTION_KINDS = ["residents_only", "no_parking", "loading_bay"];

export const LAYER_GROUPS = [
  {
    key: "parking",
    label: "Parking",
    items: [
      { key: "live", label: "Live spots", hint: "Drivers leaving now" },
      { key: "free_parking", label: "Free parking", hint: "Marked as free" },
      { key: "paid_parking", label: "Paid / public parking" },
      { key: "car_park", label: "Other car parks", hint: "Fee not stated" },
      { key: "motorcycle_parking", label: "Motorcycle parking" },
      {
        key: "community_pending",
        label: "Needs checking",
        hint: "Driver reports waiting to be confirmed on site — not parking advice yet",
      },
    ],
  },
  {
    key: "accessibility",
    label: "Accessibility",
    hint: "Blue Badge bays reported on OpenStreetMap — not verified and not a complete register. Zoom in to see them, and always check the signs on site.",
    items: [
      {
        key: "accessible",
        label: "Accessible bays",
        hint: "Blue Badge bays and car parks that report accessible bays",
      },
    ],
  },
  {
    key: "marketplace",
    label: "Marketplace",
    hint: "Private garages and driveways rented out by their owners — not public parking.",
    items: [{ key: "private", label: "Private rentals", hint: "Paid listings from owners" }],
  },
  {
    key: "restrictions",
    label: "Restrictions",
    hint: "Only verified restrictions are shown, over the exact area the source covers.",
    items: [
      { key: "residents_only", label: "Residents only" },
      { key: "no_parking", label: "No parking" },
      { key: "loading_bay", label: "Loading bays" },
    ],
  },
  {
    key: "places",
    label: "Places",
    soon: "Coming soon",
    items: [
      { key: "restaurants", label: "Restaurants", disabled: true },
      { key: "cafes", label: "Cafés", disabled: true },
      { key: "barbers", label: "Barbers", disabled: true },
    ],
  },
  {
    key: "smart",
    label: "Smart",
    soon: "Coming soon",
    items: [
      { key: "parking_chance", label: "Parking Chance", hint: "Needs more SpotDrop activity", disabled: true },
    ],
  },
];

export const DEFAULT_LAYERS = {
  live: true,
  private: false,
  accessible: false,
  free_parking: true,
  paid_parking: true,
  car_park: false,
  motorcycle_parking: false,
  community_pending: false,
  residents_only: false,
  no_parking: false,
  loading_bay: false,
  restaurants: false,
  cafes: false,
  barbers: false,
  parking_chance: false,
};

export function loadLayers() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      // One-time migration: everyone starts on the new simple default.
      localStorage.removeItem("spotdrop_map_layers_v1");
      localStorage.removeItem("spotdrop_map_layers_v2");
      return { ...DEFAULT_LAYERS };
    }
    return { ...DEFAULT_LAYERS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_LAYERS };
  }
}

export function saveLayers(layers) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(layers));
  } catch {
    /* ignore */
  }
}

export const NEARBY_RADII = [
  { key: 600, label: "Nearby" },
  { key: 1000, label: "1 km" },
  { key: 3000, label: "3 km" },
  { key: 5000, label: "5 km" },
];

export const NEARBY_PREFERENCES = [
  { key: "any", label: "Any parking" },
  { key: "free", label: "Free" },
  { key: "paid", label: "Paid / public" },
];

export function loadNearbyPrefs() {
  try {
    const raw = JSON.parse(localStorage.getItem(NEARBY_KEY) || "{}");
    return { radius: raw.radius || 1000, preference: raw.preference || "any" };
  } catch {
    return { radius: 1000, preference: "any" };
  }
}

export function saveNearbyPrefs(prefs) {
  try {
    localStorage.setItem(NEARBY_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

/** Secondary layers that are currently on (used for the badge + visual dimming). */
export function activeSecondaryCount(layers) {
  return PLACE_LAYER_KINDS.filter((k) => layers[k]).length;
}

/** One short, honest accessibility line. Dedicated bays are only ever claimed
 *  from explicit bay data; a wheelchair tag means step-free access, nothing more. */
export function accessibleLabel(accessible) {
  if (!accessible) return null;
  if (accessible.has_bays) {
    if (accessible.dedicated) {
      return accessible.bays
        ? `Blue Badge bay · ${accessible.bays} space${accessible.bays === 1 ? "" : "s"} reported`
        : "Blue Badge bay reported";
    }
    const count = accessible.bays ? `${accessible.bays} ` : "";
    return `${count}accessible bay${accessible.bays === 1 ? "" : "s"}`;
  }
  return accessible.step_free === "limited" ? "Step-free access (limited)" : "Step-free access";
}

/** Where the accessibility information comes from — never implied as verified. */
export function accessibleSource(accessible) {
  if (!accessible) return null;
  return accessible.trust || (accessible.verified ? "Verified by SpotDrop" : "OSM reported — Not verified");
}

export const KIND_META = {
  free_parking: { label: "Free parking" },
  paid_parking: { label: "Paid / public parking" },
  car_park: { label: "Car park" },
  street_parking: { label: "Street parking" },
  accessible_bay: { label: "Blue Badge bay" },
  motorcycle_parking: { label: "Motorcycle parking" },
  residents_only: { label: "Residents only" },
  no_parking: { label: "No parking" },
  loading_bay: { label: "Loading bay" },
};

export const LIVE_SPOT_TYPES = [
  { key: "normal", label: "Normal", color: "#22c55e" },
  { key: "blue_badge", label: "Blue Badge", color: "#3b82f6" },
  { key: "loading_bay", label: "Loading bay", color: "#f59e0b" },
  { key: "motorcycle", label: "Motorcycle", color: "#a855f7" },
];

export const CONFIDENCE_META = {
  confirmed: { label: "VERIFIED BY SPOTDROP", color: "#ccff00" },
  imported: { label: "OPENSTREETMAP", color: "#67e8f9" },
  reported: { label: "COMMUNITY", color: "#f59e0b" },
};
